import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	AlertStatus,
	DisabledInteractionFieldset,
	Icon,
	InputFormControl,
	ModalMainContent,
	ModalWrapper,
	SelectFormControl,
} from '../../../shared-components';
import {
	app,
	commonCw,
	editCreateEnvironment,
	systemHealthStatus,
} from '../../../../@xmcloud/core/messages/en';
import { useFormik } from 'formik';
import { editCreateEnvironmentSchema } from '../../../../@xmcloud/yup-validations';
import { useGetAllEnvironmentsLimitation } from '../../../services/environments';
import { useParams } from 'react-router-dom';
import { t } from '@transifex/native';
import {
	useComponentHealthy,
	useProjectDetails,
	useValidateConnection,
} from '../../../../@xmcloud/hooks';
import { onDisabled } from '../../../../@xmcloud/utils/helpers';
import { TenantType } from '../../../../@xmcloud/types';
import { ADO_BRANCH_PREFIX } from '../../../../@xmcloud/utils/constants';
import { useGetGitHubPaginatedBranches } from '../../../services/githubApi';
import { useGetADOBranches } from '../../../services/adoApi';
import { debounce } from 'lodash';
import { mdiAlert } from '@mdi/js';
import { useOrgProdCheckboxState } from '../../../services/organizations';

const { NonProduction, Production } = TenantType;

const {
	environmentName,
	isThisProduction,
	linkToBranch,
	triggerDeployment,
	editTitle,
	createTitle,
	yes,
	no,
	branchHelperText1,
	branchHelperText2,
	errorPostfix,
	warningSuffix,
} = editCreateEnvironment;

const { retry } = commonCw;
const { save, create } = app;
const { errMsg3, errMsg4, envCreate, envEdit } = systemHealthStatus;

interface Props {
	openModal: boolean;
	setOpenModal: (a: boolean) => void;
	isEdit?: boolean;
	onSubmit: (values: any) => void;
	isLoading?: boolean;
	editInitialValues?: EditCreateEnvironmentFormProps;
	shouldRetry?: boolean;
}

const NAME = 'name';
const TENANT_TYPE = 'tenantType';
const REPOSITORY_BRANCH = 'repositoryBranch';
const DEPLOY_ON_COMMIT = 'deployOnCommit';

const EditCreateEnvironmentForm: FC<Props> = ({
	openModal,
	setOpenModal,
	isEdit = false,
	onSubmit,
	isLoading = false,
	editInitialValues,
	shouldRetry = false,
}) => {
	const [areActionsDisabled, setAreActionsDisabled] = useState(false);
	const { projectId } = useParams<{ projectId: string }>();
	const inputQuery = useRef('');
	const modalBodyRef = useRef<HTMLDivElement | null>(null);
	const hasObservedLoadingSinceSubmitRef = useRef(false);

	const {
		sourceControlIntegrationId,
		repository,
		sourceControlIntegrationName,
	} = useProjectDetails(projectId!);
	const owner = sourceControlIntegrationName;

	const { data: allEnvironmentsLimitation } =
		useGetAllEnvironmentsLimitation(!isEdit);

	const validationSchema = editCreateEnvironmentSchema(
		allEnvironmentsLimitation,
		isEdit,
	);

	const initialValues = useRef(
		isEdit && editInitialValues
			? editInitialValues
			: {
					name: '',
					tenantType: NonProduction,
					repositoryBranch: '',
					deployOnCommit: false,
					linkEnvironmentToDefaultBranch: false,
				},
	);
	const {
		isLoading: isValidationLoading,
		isNotValidIntegration,
		integrationIdValidationMsg,
		provider,
		token,
	} = useValidateConnection({
		sourceControlIntegrationId,
	});

	const {
		isBlockersHealthy,
		isAdoProvider,
		isGithubProvider,
		isLoading: isHealthStatusLoading,
		isCurrentProviderHealthy,
		getUnhealthyBlockerList,
	} = useComponentHealthy({ provider: provider || 0 });

	const { showCheckbox: canCreateProdEnv } = useOrgProdCheckboxState();

	const isProviderUnHealthy =
		!isCurrentProviderHealthy || isNotValidIntegration;

	const hasProvider = isAdoProvider || isGithubProvider;

	const integrationErrorMessage = !isCurrentProviderHealthy
		? `${isAdoProvider ? 'Azure DevOps' : 'GitHub'} is not healthy.`
		: integrationIdValidationMsg;

	const disableCreateBtn = !isBlockersHealthy && !isEdit;

	const listOfBlockers = [
		...(!isBlockersHealthy && !isEdit
			? [...getUnhealthyBlockerList()]
			: []),
	];

	const showErrorAlert =
		listOfBlockers.length && disableCreateBtn && !isHealthStatusLoading;

	const showWarningAlert =
		isProviderUnHealthy &&
		!isHealthStatusLoading &&
		!showErrorAlert &&
		!isValidationLoading &&
		hasProvider;

	const {
		data: gitRepositoryBranchData,
		isLoading: isGitBranchLoading,
		hasMore,
		isFetching: isFetchingGithubBranches,
		fetchNextPage: fetchNextPageGithubBranches,
	} = useGetGitHubPaginatedBranches({
		token,
		owner: owner!,
		repo: repository!,
		_enabled: isGithubProvider,
		onSuccess: onGithubBranchesSuccess,
	});

	let splitRepoInfo = null;
	if (isAdoProvider) {
		splitRepoInfo = repository?.split('/');
	}

	const { data: adoRepositoryBranchData, isLoading: isAdoBranchLoading } =
		useGetADOBranches(
			token,
			splitRepoInfo ? splitRepoInfo[0] : '', // Organization
			splitRepoInfo ? splitRepoInfo[1] : '', // Project
			splitRepoInfo ? splitRepoInfo[2] : '', // Repository
			isAdoProvider,
		);

	const isRepositoryBranchLoading =
		isGitBranchLoading || isValidationLoading || isAdoBranchLoading;

	const repositoryBranchOptions = useMemo(() => {
		const adoBranches = adoRepositoryBranchData?.data.value || [];
		const githubBranches = gitRepositoryBranchData || [];

		const repos = isAdoProvider
			? adoBranches
			: isGithubProvider
				? githubBranches
				: [];

		const mappedAdoBranches = repos.map((branch: any) => ({
			label: branch.name.replace(ADO_BRANCH_PREFIX, ''),
			value: branch.name.replace(ADO_BRANCH_PREFIX, ''),
		}));

		const noneRepo = mappedAdoBranches.find((b: any) => b.label === 'None');

		if (!noneRepo?.label) {
			mappedAdoBranches.push({ label: 'None', value: '' });
		}

		return mappedAdoBranches;
	}, [
		adoRepositoryBranchData?.data.value,
		gitRepositoryBranchData,
		isAdoProvider,
		isGithubProvider,
	]);

	function onGithubBranchesSuccess(lastPage: any) {
		const query = inputQuery.current;

		if (query === '' || !hasMore || !isGithubProvider || !lastPage.length)
			return;

		const hasRepo = lastPage.some((r: any) => r.name.includes(query));

		if (!hasRepo) {
			fetchNextPageGithubBranches();
		}
	}

	const onInputChange = useCallback(
		(e: string) => {
			if (inputQuery) {
				inputQuery.current = e;
			}

			if (
				e === '' ||
				!hasMore ||
				!isGithubProvider ||
				!repositoryBranchOptions?.length
			)
				return;

			const hasRepo = repositoryBranchOptions.some((r: any) =>
				r.label.includes(e),
			);

			if (!hasRepo) {
				fetchNextPageGithubBranches();
			}
		},
		[
			fetchNextPageGithubBranches,
			hasMore,
			isGithubProvider,
			repositoryBranchOptions,
		],
	);

	const debouncedChangeHandler = useMemo(
		() => debounce(onInputChange, 300),
		[onInputChange],
	);

	const {
		errors,
		values,
		handleChange,
		handleSubmit: formikHandleSubmit,
		setFieldValue,
		setFieldTouched,
		touched,
		isValid,
	} = useFormik({
		enableReinitialize: true,
		initialValues: initialValues.current,
		validationSchema,
		validateOnBlur: false,
		onSubmit: async (values) => {
			setAreActionsDisabled(true);
			try {
				await Promise.resolve(onSubmit(values));
			} catch (error) {
				setAreActionsDisabled(false);
				throw error;
			}
		},
	});

	useEffect(() => {
		if (!openModal) {
			setAreActionsDisabled(false);
		}
	}, [openModal]);

	useEffect(() => {
		if (areActionsDisabled && shouldRetry) {
			setAreActionsDisabled(false);
		}
	}, [areActionsDisabled, shouldRetry]);

	useEffect(() => {
		if (
			values.repositoryBranch === '' &&
			!isEdit &&
			repositoryBranchOptions.length
		) {
			const masterOrMainBranch = repositoryBranchOptions.find(
				(b: any) => b.value === 'main' || b.value === 'master',
			);

			const repositoryBranch: string = Boolean(masterOrMainBranch)
				? masterOrMainBranch?.value
				: repositoryBranchOptions[0].value;

			setFieldValue(REPOSITORY_BRANCH, repositoryBranch);
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [repositoryBranchOptions]);

	const isFormLoading =
		isLoading ||
		isRepositoryBranchLoading ||
		isValidationLoading ||
		isHealthStatusLoading;

	useEffect(() => {
		if (!openModal || !areActionsDisabled) {
			hasObservedLoadingSinceSubmitRef.current = false;
			return;
		}

		if (isFormLoading) {
			hasObservedLoadingSinceSubmitRef.current = true;
			return;
		}

		if (!shouldRetry && hasObservedLoadingSinceSubmitRef.current) {
			setAreActionsDisabled(false);
		}
	}, [areActionsDisabled, isFormLoading, openModal, shouldRetry]);
	const handleClose = useCallback(() => {
		if (areActionsDisabled) {
			return;
		}
		setOpenModal(false);
	}, [areActionsDisabled, setOpenModal]);

	const isDisabled = isEdit
		? onDisabled(isValid, values, editInitialValues) || isFormLoading
		: disableCreateBtn;

	return (
		<ModalWrapper
			isOpen={openModal}
			title={isEdit ? editTitle : createTitle}
			onClose={handleClose}
			isCloseDisabled={areActionsDisabled}
			disableInteraction={areActionsDisabled}
			closeOnOverlayClick={!areActionsDisabled}
			closeOnEsc={!areActionsDisabled}
			scrollContainerRef={modalBodyRef}
		>
			<form
				data-testid="edit-create-environment-form"
				onSubmit={formikHandleSubmit}
			>
				<ModalMainContent
					onClose={handleClose}
					rightButtonText={
						shouldRetry ? retry : isEdit ? save : create
					}
					isLoading={isFormLoading}
					isDisabled={isDisabled || areActionsDisabled}
					isCloseDisabled={areActionsDisabled}
					bodyRef={modalBodyRef}
					bodyTabIndex={-1}
				>
					<DisabledInteractionFieldset disabled={areActionsDisabled}>
						<InputFormControl
							{...{
								isInvalid: Boolean(errors.name && touched.name),
								label: environmentName,
								name: NAME,
								value: values.name,
								onChange: handleChange,
								error: errors.name,
								onFocus: () => setFieldTouched(NAME, true),
							}}
						/>
						{canCreateProdEnv && (
							<SelectFormControl
								{...{
									showAsPlaceHolder: isEdit,
									error: errors.tenantType,
									isInvalid: Boolean(errors.tenantType),
									options: tenantTypeOptions,
									onChange: (e: any) => {
										setFieldValue(TENANT_TYPE, e.value);
									},
									label: isThisProduction,
									name: TENANT_TYPE,
									currentValue: values.tenantType,
									helperText: branchHelperText1,
									helperBoldText: branchHelperText2,
									helperTextIcon: (
										<Icon
											path={mdiAlert}
											color="warning"
											mb={0.5}
											mr={1}
										/>
									),
								}}
							/>
						)}
						<SelectFormControl
							{...{
								error: errors.repositoryBranch,
								isInvalid: Boolean(errors.repositoryBranch),
								isLoading:
									isRepositoryBranchLoading ||
									isFetchingGithubBranches,
								options: repositoryBranchOptions,
								onChange: (e: any) => {
									setFieldValue(REPOSITORY_BRANCH, e.value);
									if (e.value === '')
										setFieldValue(DEPLOY_ON_COMMIT, false);
								},
								label: linkToBranch,
								name: REPOSITORY_BRANCH,
								currentValue: values.repositoryBranch,
								isDisabled:
									!Boolean(
										repositoryBranchOptions.length > 1,
									) || isProviderUnHealthy,
								onInputChange: debouncedChangeHandler,
								onMenuScrollToBottom: () => {
									if (
										hasMore &&
										isGithubProvider &&
										!isFetchingGithubBranches
									) {
										fetchNextPageGithubBranches();
									}
								},
							}}
						/>
						<SelectFormControl
							{...{
								error: errors.deployOnCommit,
								isInvalid: Boolean(errors.deployOnCommit),
								options: deployOnCommitOptions,
								onChange: (e: any) => {
									setFieldValue(DEPLOY_ON_COMMIT, e.value);
								},
								label: triggerDeployment,
								name: DEPLOY_ON_COMMIT,
								currentValue: values.deployOnCommit,
								isDisabled:
									!Boolean(values.repositoryBranch) ||
									isProviderUnHealthy,
							}}
						/>
						{Boolean(showWarningAlert) && (
							<Alert
								description={`${integrationErrorMessage}${
									isEdit ? warningSuffix : errorPostfix
								}`}
								status="warning"
								marginTop={4}
								boldTextTransform="none"
								data-testid="warning-alert-component"
							/>
						)}
						{!!showErrorAlert && (
							<AlertStatus
								description={
									listOfBlockers.length > 1
										? errMsg3
										: errMsg4
								}
								status="error"
								data-testid="error-alert-component"
								variableTxt={listOfBlockers.join(', ')}
								marginTop={4}
								variableTxt2={isEdit ? envEdit : envCreate}
							/>
						)}
						{!canCreateProdEnv && Boolean(errors.tenantType) && (
							<Alert
								description={String(errors.tenantType)}
								status="error"
								marginTop={4}
								data-testid="tenant-type-validation-alert"
							/>
						)}
					</DisabledInteractionFieldset>
				</ModalMainContent>
			</form>
		</ModalWrapper>
	);
};

export default EditCreateEnvironmentForm;

const deployOnCommitOptions = [
	{ label: t(no), value: false },
	{ label: t(yes), value: true },
];

const tenantTypeOptions = [
	{ label: t(no), value: NonProduction },
	{ label: t(yes), value: Production },
];

export type EditCreateEnvironmentFormProps = {
	name: string;
	tenantType: TenantType;
	repositoryBranch: string;
	deployOnCommit: boolean;
	linkEnvironmentToDefaultBranch: boolean;
	cmEnvironmentId?: string;
};
